import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import api from "../api/axiosInstance";
import { clearAuthToken, clearUserSpecificStorage, getAuthToken, setAuthToken } from "../utils/storageKeys";
import { clearCache } from "../components/NewDashboard/widgets/widgetDataUtils";
import { clearSvgCache } from "../utils/svgCache";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeName, setWelcomeName] = useState("");
  const [welcomeRunId, setWelcomeRunId] = useState(0);
  const welcomeActive = useRef(false);

  const startWelcome = (name) => {
    if (name) setWelcomeName(name);
    welcomeActive.current = true;
    setWelcomeRunId((run) => run + 1);
    setShowWelcome(true);
  };

  useEffect(() => {
    const token = getAuthToken();
    const demoMode = localStorage.getItem("demoMode");
    
    // Clear old demo mode tokens
    if (demoMode === "true" || token === "demo-token-123") {
      clearAuthToken();
      localStorage.removeItem("demoMode");
      setLoading(false);
      return;
    }
    
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      console.error("Error fetching user:", error);
      // If token is invalid, clear it
      clearAuthToken();
      delete api.defaults.headers.common["Authorization"];
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await api.post("/auth/login", {
        username,
        password,
      });
      const { token, user: userData } = response.data;

      if (!token) {
        throw new Error("No token received from server");
      }

      setAuthToken(token);
      localStorage.removeItem("demoMode");
      delete api.defaults.headers.common["Authorization"];
      
      setUser(userData);
      setWelcomeName(userData.username || userData.name || "");
      if (!welcomeActive.current) startWelcome(userData.username || userData.name || "");
      return true;
    } catch (error) {
      console.error("❌ Login error:", error.response?.data || error.message);
      const detail = error.response?.data?.detail;
      // detail can be a non-string (e.g. FastAPI 422 validation array) — only toast strings
      const errorMessage = (typeof detail === "string" && detail) || error.message || "Login failed";
      toast.error(errorMessage);
      return false;
    }
  };

  const register = async (userData) => {
    try {
      await api.post("/auth/register", userData);
      toast.success("Registration successful! Please login.");
      return true;
    } catch (error) {
      console.error("❌ Registration error:", error.response?.data || error.message);
      const detail = error.response?.data?.detail;
      // detail can be a non-string (e.g. FastAPI 422 validation array) — only toast strings
      const errorMessage = (typeof detail === "string" && detail) || error.message || "Registration failed";
      toast.error(errorMessage);
      return false;
    }
  };

  const logout = async () => {
    welcomeActive.current = false;
    setShowWelcome(false);
    setWelcomeName("");
    setWelcomeRunId(0);

    // Revoke JWT server-side before clearing client auth
    try {
      await api.post("/auth/logout");
    } catch {
      // Still clear local auth even if revoke fails (offline / already expired)
    }

    clearAuthToken();
    
    // Clear all user-specific localStorage items (centralized)
    clearUserSpecificStorage();

    // Clear in-memory caches (widget data + SVG cache)
    clearCache();
    clearSvgCache();
    
    // Clear preferences cache in WidgetDashboard (module-level variable)
    // Note: This is a best-effort cleanup since it's in a different module
    // The component's useEffect will handle the rest
    try {
      // Clear any module-level caches if they exist
      if (typeof window !== 'undefined' && window.__clearPreferencesCache) {
        window.__clearPreferencesCache();
      }
    } catch (error) {
      // Ignore - component will handle cleanup
    }
    
    // Remove authorization header
    delete api.defaults.headers.common["Authorization"];
    
    // Clear user state
    setUser(null);
    
    toast.success("Logged out successfully");
  };

  const dismissWelcome = () => {
    welcomeActive.current = false;
    setShowWelcome(false);
  };

  const updateUser = (patch) => {
    setUser((u) => (u ? { ...u, ...patch } : u));
  };

  const acceptLegal = async (acceptedLegal) => {
    const response = await api.post("/auth/legal/accept", acceptedLegal);
    setUser(response.data);
    return response.data;
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    showWelcome,
    welcomeName,
    welcomeRunId,
    startWelcome,
    dismissWelcome,
    updateUser,
    acceptLegal,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
